import React, { useState } from "react";
import axios from "axios";
import { Seo } from "@/components/Seo";
import { HeartHandshake, ArrowRight, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The public face of civichearth.com until launch (COMING_SOON=true on the
// backend drives this — see memory/HANDOFF.md). No nav, no login, no app:
// brand, mission, and an email-interest capture that only stores.
export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [outcode, setOutcode] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | done | error
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || state === "busy") return;
    setState("busy");
    setError("");
    try {
      await axios.post(`${API}/interest`, { email, outcode: outcode.trim() });
      setState("done");
    } catch (err) {
      setState("error");
      setError(err?.response?.data?.detail || "Something went wrong — try again in a moment.");
    }
  };

  return (
    <div data-testid="coming-soon-page" className="min-h-screen bg-forest flex flex-col items-center justify-center px-6 text-center">
      <Seo
        title="CivicHearth — Coming soon"
        description="A street that looks after itself. CivicHearth connects neighbours, volunteers and local causes. Launching soon."
      />
      <div className="flex items-center gap-3 animate-fade-up">
        <span className="w-11 h-11 rounded-full bg-cream flex items-center justify-center">
          <HeartHandshake className="w-5 h-5 text-forest" />
        </span>
        <span className="font-display text-2xl tracking-tight text-cream">
          Civic<span className="text-terracotta">.</span>Hearth
        </span>
      </div>

      <div className="mt-10 text-[11px] uppercase tracking-[0.3em] text-terracotta font-bold animate-fade-up" style={{ animationDelay: "120ms" }}>
        Coming soon
      </div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-cream tracking-tight max-w-2xl leading-tight animate-fade-up" style={{ animationDelay: "220ms" }}>
        A street that looks after itself.
      </h1>
      <p className="mt-5 text-cream/70 text-base max-w-md leading-relaxed animate-fade-up" style={{ animationDelay: "320ms" }}>
        CivicHearth connects neighbours, volunteers and local causes — every hour counted, every kindness shared.
      </p>

      <div className="mt-10 w-full max-w-sm animate-fade-up" style={{ animationDelay: "420ms" }}>
        {state === "done" ? (
          <div data-testid="interest-success" className="flex items-center justify-center gap-2 rounded-full border border-cream/25 px-5 py-3 text-cream text-sm">
            <Check className="w-4 h-4 text-terracotta" /> You're on the list — we'll let you know.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-2">
            <div className="flex gap-2">
              <input
                data-testid="interest-email"
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 min-w-0 rounded-full bg-cream/10 border border-cream/25 px-5 py-3 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-terracotta"
              />
              <button
                data-testid="interest-submit"
                type="submit"
                disabled={state === "busy"}
                className="rounded-full bg-terracotta text-white px-5 py-3 text-sm font-semibold hover:brightness-110 transition-all inline-flex items-center gap-1.5 shrink-0"
              >
                {state === "busy" ? "…" : <>Keep me posted <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
            <input
              data-testid="interest-outcode"
              type="text"
              maxLength={4}
              placeholder="Outward postcode, e.g. FY1 (optional — for local updates)"
              value={outcode}
              onChange={(e) => setOutcode(e.target.value)}
              className="w-full rounded-full bg-cream/10 border border-cream/25 px-5 py-3 text-sm text-cream placeholder:text-cream/40 focus:outline-none focus:border-terracotta"
            />
          </form>
        )}
        {state === "error" && <div data-testid="interest-error" className="mt-3 text-xs text-terracotta">{error}</div>}
        <p className="mt-4 text-[11px] text-cream/40">No spam — one email when we open the doors.</p>
      </div>
    </div>
  );
}
